import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { AuthProvider } from "@/contexts/AuthContext";
import { CmsProvider } from "@/contexts/CmsContext";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

const Home = lazy(() => import("@/pages/Home"));
const Bijoux = lazy(() => import("@/pages/Bijoux"));
const Collection = lazy(() => import("@/pages/Collection"));
const CommentCaMarche = lazy(() => import("@/pages/CommentCaMarche"));
const APropos = lazy(() => import("@/pages/APropos"));
const Commander = lazy(() => import("@/pages/Commander"));
const Merci = lazy(() => import("@/pages/Merci"));
const PolitiqueConfidentialite = lazy(() => import("@/pages/PolitiqueConfidentialite"));
const ConditionsGenerales = lazy(() => import("@/pages/ConditionsGenerales"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminPages = lazy(() => import("@/pages/admin/AdminPages"));
const AdminPageEdit = lazy(() => import("@/pages/admin/AdminPageEdit"));
const AdminCollections = lazy(() => import("@/pages/admin/AdminCollections"));
const AdminCollectionEdit = lazy(() => import("@/pages/admin/AdminCollectionEdit"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminPublish = lazy(() => import("@/pages/admin/AdminPublish"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-2 border-brand-secondary/20" />
          <div className="absolute inset-0 rounded-full border-2 border-t-brand-primary animate-spin" />
        </div>
        <p className="text-xs font-body text-brand-body/40 tracking-wide">Chargement...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <CmsProvider>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes with site layout */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/bijoux" element={<Bijoux />} />
              <Route path="/bijoux/:slug" element={<Collection />} />
              <Route path="/comment-ca-marche" element={<CommentCaMarche />} />
              <Route path="/a-propos" element={<APropos />} />
              <Route path="/commander" element={<Commander />} />
              <Route path="/merci" element={<Merci />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/conditions-generales" element={<ConditionsGenerales />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
            </Route>

            {/* Admin routes — no site header/footer */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>}
            />
            <Route
              path="/admin/pages"
              element={<ProtectedRoute><AdminPages /></ProtectedRoute>}
            />
            <Route
              path="/admin/pages/:slug"
              element={<ProtectedRoute><AdminPageEdit /></ProtectedRoute>}
            />
            <Route
              path="/admin/collections"
              element={<ProtectedRoute><AdminCollections /></ProtectedRoute>}
            />
            <Route
              path="/admin/collections/:slug"
              element={<ProtectedRoute><AdminCollectionEdit /></ProtectedRoute>}
            />
            <Route
              path="/admin/settings"
              element={<ProtectedRoute><AdminSettings /></ProtectedRoute>}
            />
            <Route
              path="/admin/publish"
              element={<ProtectedRoute><AdminPublish /></ProtectedRoute>}
            />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </CmsProvider>
    </BrowserRouter>
  );
}

export default App;
